import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api, ApiError } from '@/api/client';

export type CurrentUser = {
  email: string;
  name: string;
  custid: string;
};

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const initialized = ref(false);
  const lastError = ref<string | null>(null);

  async function bootstrap(): Promise<void> {
    if (initialized.value) return;
    try {
      user.value = await api<CurrentUser>('/auth/me');
    } catch (err) {
      user.value = null;
      if (!(err instanceof ApiError && err.status === 401)) {
        // unknown error — log to console for debugging
        // eslint-disable-next-line no-console
        console.warn('auth bootstrap failed', err);
      }
    } finally {
      initialized.value = true;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    lastError.value = null;
    try {
      user.value = await api<CurrentUser>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        lastError.value = err.status === 429 ? 'too_many_attempts' : err.code;
      } else {
        lastError.value = 'network_error';
      }
      throw err;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      user.value = null;
    }
  }

  return { user, initialized, lastError, bootstrap, login, logout };
});
